import {
    actionChannel, all, call, cancel, cancelled, fork, put, select, take, takeLatest,
} from 'redux-saga/effects';
import {
    WorkspaceOverlayConfiguration,
    WorkspacesOverlayTargets,
    WorkspacesOverlayTypes,
} from 'common/store/workspaces/types';
import { setWorkspaceOverlay } from 'common/store/workspaces/actions';
import { delay, Task } from 'redux-saga';
import { tl } from 'common/services/i18nService';
import { loadMissingProperties } from 'common/store/context/actions';
import { pushAlert, removeAlert } from 'common/components/notifications/actions';
import { AlertLevel, Alert } from 'common/components/notifications/types';
import { Context } from 'common/store/common/constants';
import { TicketData } from 'common/services/upload/UploadService';
import { SessionState } from 'common/store/session/reducer';
import { TypeKeys as FileUploadTypeKeys } from 'common/store/fileUpload/actions';
import { SemanticICONS } from 'semantic-ui-react';
import { ExportFormat, OptionMenuActions, OptionsMenuActionsName } from './types';
import { ContextTypeKeys } from '../assessment/actions';
import { ContextState } from '../context/reducer';
import apiService from '../../services/api/apiService';
import { getAssessment, getContext } from '../assessment/sagas';
import {
    apiCallIsFinished,
    fileIsReadyForDownload,
    onCloseOptionsMenu,
    onDownloadApiCall,
    onFileIsUploaded,
    onGradeIsUpdated, onMarkIsUpdated,
    onMarkSchemeFileUploaded,
    resetMarkSchemeFileUpload,
} from './actions';
import { fileUploadService } from '../../services/upload/fileUploadService';
import { onTabChange } from '../../features/workflowOverlay/actions';
import { workflowTabs } from '../../features/workflowOverlay/reducer';
import { getCommitteeById, Committee } from '../../services/committeeService';
import { getWorkflowTabIndexByActionsMenu } from '../../services/optionsMenuItemsService';
import { RootState } from '../rootReducer';
import { GradingWorkflowSettingsTypeKeys } from '../../features/workflowSettings/types';
import { getPartyCommitteeIdForContext } from '../../services/gradingPartyService';
import { CommitteeType } from '../assessment/constants';
import { AssessmentState } from '../assessment/reducer';
import { getSessionState } from '../context/sagas';
import { updateWorkflowInfo } from '../context/actions';
import notificationService from '../../services/notificationService';
import { FileErrorTypes, FileType, FileUploadInfo } from '../common/constants';

let fileUploadTask: Task;

function* handleOpenOverlay(name: string, configuration: WorkspaceOverlayConfiguration) {
    const { workspaceTitle, workspaceSecondaryTitle, options } = configuration;

    try {
        yield put(setWorkspaceOverlay(name, WorkspacesOverlayTargets.APP_OVERLAY_TARGET, {
            workspaceTitle,
            workspaceSecondaryTitle,
            options,
        }));
        // close the options menu after opening the overlay
        yield put(onCloseOptionsMenu());
    } catch (e) {
        console.error(e.message);
    }
}

function* handleWorkflowOverlayTabChange(actionName: OptionsMenuActionsName) {
    const contextState: ContextState = yield select(getContext);
    const featureId = `feature_${WorkspacesOverlayTypes.WORKFLOW_OVERLAY}_${contextState.workspaceId}_overlay`;
    switch (actionName) {
    case OptionsMenuActionsName.OPEN_WORKFLOW_IN_SHARE:
        yield put(onTabChange(featureId, workflowTabs.SHARE.index));
        break;
    case OptionsMenuActionsName.OPEN_WORKFLOW_IN_ALIGN:
        yield put(onTabChange(featureId, workflowTabs.ALIGN.index));
        break;
    case OptionsMenuActionsName.OPEN_WORKFLOW_IN_CONFIRM:
        yield put(onTabChange(featureId, workflowTabs.CONFIRM.index));
        break;
    case OptionsMenuActionsName.OPEN_WORKFLOW_SETTINGS:
        yield put(onTabChange(featureId, workflowTabs.SETUP.index));
        break;
    case OptionsMenuActionsName.MANAGE_COMMITTEE_MEMBERS:
        yield put(onTabChange(featureId, workflowTabs.MEMBERS.index));
        break;
    }
}

function* downloadAndPrintQuestionSet() {
    const workspaceOverlayName = WorkspacesOverlayTypes.DOWNLOAD_AND_PRINT;
    const options = {
        withoutHeader: true,
        context: Context.PREVIEW,
        title: tl('grading.printOverlay.printManager'),
    };
    yield call(handleOpenOverlay, workspaceOverlayName, { options });
}

function* callOpenDownloadAndPrintOverlay(actionName: OptionsMenuActionsName) {
    const contextState: ContextState = yield select(getContext);
    const {
        workflowInfo, partyId, partyState, selectedCommitteeId,
    } = contextState;
    const workspaceOverlayName = WorkspacesOverlayTypes.DOWNLOAD_AND_PRINT;
    let numberOfSelectedSubmissions;

    if (actionName === OptionsMenuActionsName.DOWNLOAD_AND_PRINT_COMMITTEE_SUBMISSIONS_PDF) {
        const currentCommittee = getCommitteeById(selectedCommitteeId);
        numberOfSelectedSubmissions = currentCommittee?.candidateCount || workflowInfo.numberOfCandidatesForContext;
    } else {
        numberOfSelectedSubmissions = workflowInfo.numberOfSubmissionsForContext;
    }
    const options = {
        withoutHeader: true,
        downloadAndPrintAllSubmissions: true,
        numberOfSelectedSubmissions,
        exportAllSubmissions: true,
    };
    if (!(partyId > 0 && partyState.partyName)) {
        yield put(loadMissingProperties(['partyId', 'partyState']));
    }

    yield call(handleOpenOverlay, workspaceOverlayName, { options });
}

function* callOpenWorkflowOverlay(actionName: OptionsMenuActionsName) {
    const assessmentState: AssessmentState = yield select(getAssessment);
    const { evaluators } = assessmentState;
    const contextState: ContextState = yield select(getContext);
    const { partyState: { gradingPartyDocument } } = contextState;
    const selectedCommitteeId = getPartyCommitteeIdForContext(gradingPartyDocument);
    const workspaceOverlayName = WorkspacesOverlayTypes.WORKFLOW_OVERLAY;
    let workspaceTitle = tl('grading.overlay.workflow.title');
    let workspaceSecondaryTitle = null;

    if (evaluators && evaluators.length > 1) {
        workspaceTitle += ` ${tl('grading.overlay.workflow.forGraders')}`;
    } else if (evaluators && evaluators.length === 1) {
        workspaceTitle += ` ${evaluators[0].name}`;
    } else if (selectedCommitteeId) {
        const committee: Committee = getCommitteeById(selectedCommitteeId);
        workspaceTitle += ` ${tl('grading.overlay.workflow.forCommittee')} ${committee?.name}`;
        workspaceSecondaryTitle = committee?.type === CommitteeType.grading
            ? tl('grading.committees.grading')
            : tl('grading.committees.appeal');
    }

    const options = { defaultTabIndex: getWorkflowTabIndexByActionsMenu(actionName) };
    yield call(handleOpenOverlay, workspaceOverlayName, {
        workspaceTitle,
        workspaceSecondaryTitle,
        options,
    });
    yield call(handleWorkflowOverlayTabChange, actionName);
}

function* callOpenThresholdOverlay(actionName: OptionsMenuActionsName) {
    const workspaceOverlayName = WorkspacesOverlayTypes.WORKFLOW_SETTINGS;
    const workspaceTitle = tl('grading.overlay.workflowSettings.title');
    const options = {
        defaultTabIndex: actionName === OptionsMenuActionsName.OPEN_GRADES_THRESHOLD_EDITOR_UPDATE ? 2 : 1,
        gradingWorkflowSettingsTabsGroup: GradingWorkflowSettingsTypeKeys.WEIGHTS_AND_THRESHOLDS,
    };
    yield call(handleOpenOverlay, workspaceOverlayName, { workspaceTitle, options });
}

function* callOpenQuestionWeightsOverlay() {
    const workspaceOverlayName = WorkspacesOverlayTypes.WORKFLOW_SETTINGS;
    const workspaceTitle = tl('grading.overlay.workflowSettings.title');
    const options = { defaultTabIndex: 0, gradingWorkflowSettingsTabsGroup: GradingWorkflowSettingsTypeKeys.WEIGHTS_AND_THRESHOLDS };
    yield call(handleOpenOverlay, workspaceOverlayName, { workspaceTitle, options });
}

function* callOpenQuestionAssignmentsOverlay() {
    const workspaceOverlayName = WorkspacesOverlayTypes.WORKFLOW_SETTINGS;
    const workspaceTitle = tl('grading.overlay.workflowSettings.title');
    const options = { defaultTabIndex: 0, gradingWorkflowSettingsTabsGroup: GradingWorkflowSettingsTypeKeys.QUESTION_ASSIGNMENTS };
    yield call(handleOpenOverlay, workspaceOverlayName, { workspaceTitle, options });
}

function* callOpenQuestionAnswerKeyCorrectionOverlay() {
    const workspaceOverlayName = WorkspacesOverlayTypes.WORKFLOW_SETTINGS;
    const workspaceTitle = tl('grading.overlay.workflowSettings.title');
    const options = {
        defaultTabIndex: 0,
        gradingWorkflowSettingsTabsGroup: GradingWorkflowSettingsTypeKeys.QUESTION_ANSWER_KEYS,
        fullHeight: true,
    };
    yield call(handleOpenOverlay, workspaceOverlayName, { workspaceTitle, options });
}

function* callOpenCommitteeSelectOverlay() {
    const workspaceOverlayName = WorkspacesOverlayTypes.COMMITTEE_SELECTOR;
    const workspaceTitle = tl('grading.overlay.committeesSelector.title');
    const options = { };

    yield call(handleOpenOverlay, workspaceOverlayName, { workspaceTitle, options });
}

export function* generateFile(format: string, downloadType?: string, params?: { [key: string]: unknown }): any {
    yield put(onDownloadApiCall());
    const optionsMenuState = yield select((state: RootState) => state.optionsMenu);

    if (optionsMenuState.timesOfDownloadApiCalls > 3) {
        yield put(apiCallIsFinished());
        yield cancel();
    }
    const controller = new AbortController();
    const contextState: ContextState = yield select(getContext);
    const { partyState: { gradingPartyDocument }, assessmentRunId } = contextState;
    const committeeId = getPartyCommitteeIdForContext(gradingPartyDocument);
    let updatedParams = params || {};
    if (committeeId > 0) {
        updatedParams = { ...updatedParams, committeeId };
    }
    try {
        const task = yield call(apiService.submitTask,
            assessmentRunId,
            format,
            'assessment',
            '',
            controller.signal,
            downloadType,
            updatedParams);
        while (true) {
            const status = yield call(apiService.checkStatus, task.jobId, controller.signal);
            yield delay(2000);
            if (status && status.progress === 100) {
                yield call(downloadFile, format, downloadType);
                break;
            }
        }
    } catch (e) {
        console.error(e.message);
        yield cancel();
    } finally {
        if (yield cancelled()) {
            controller.abort();
        }
    }
}

function* downloadFile(format: string, downloadType?: string) {
    const contextState: ContextState = yield select(getContext);
    const controller = new AbortController();
    const { partyState: { gradingPartyDocument } } = contextState;
    const partyCommitteeIdForContext = getPartyCommitteeIdForContext(gradingPartyDocument);
    try {
        const data = yield call(apiService.getAssessmentRunExports, contextState.assessmentRunId, partyCommitteeIdForContext, controller.signal);

        if (data && data[format] && data[format].contentUrl.includes('https')) {
            yield put(fileIsReadyForDownload());
            window.open(data[format].contentUrl, '_self');
        } else {
            yield call(generateFile, format, downloadType);
        }
    } catch (e) {
        console.error(e.message);
        yield cancel();
    } finally {
        if (yield cancelled()) {
            controller.abort();
        }
    }
}

function* callGetAssessmentRunExports(actionName: OptionsMenuActionsName) {
    switch (actionName) {
    case OptionsMenuActionsName.DOWNLOAD_MARKS_AS_EXCEL:
        yield call(generateFile, ExportFormat.EXCEL);
        break;
    case OptionsMenuActionsName.DOWNLOAD_MARKS_AND_GRADES_AS_CSV:
        yield call(generateFile, ExportFormat.SCORES);
        break;
    case OptionsMenuActionsName.DOWNLOAD_MULTIPLE_ATTEMPTS_ALL_DATA_AS_CSV:
        yield call(generateFile, ExportFormat.MULTIPLE_ATTEMPTS_CSV, 'ALL_DATA');
        break;
    case OptionsMenuActionsName.DOWNLOAD_MULTIPLE_ATTEMPTS_FINAL_DATA_AS_CSV:
        yield call(generateFile, ExportFormat.MULTIPLE_ATTEMPTS_CSV, 'FINAL_DATA');
        break;
    case OptionsMenuActionsName.DOWNLOAD_TOTAL_MARKS_AND_GRADES_AS_CSV:
        yield call(generateFile, ExportFormat.TOTAL_MARKS_AND_GRADES_SUMMARY);
        break;
    case OptionsMenuActionsName.DOWNLOAD_MARKS_AND_CRITERIA:
        yield call(generateFile, ExportFormat.MARKS_AND_CRITERIA);
        break;
    }
}

function* callDownloadExcelMarkingSheet() {
    const controller = new AbortController();
    const contextState: ContextState = yield select(getContext);
    const { assessmentRunId, selectedCommitteeId } = contextState;
    try {
        const res = yield call(apiService.exportEmptyMarkingSheet,
            assessmentRunId,
            selectedCommitteeId,
            controller.signal);
        if (res && res.fileDownloaded) {
            yield put(fileIsReadyForDownload());
        }
    } catch (e) {
        console.error(e.message);
        yield cancel();
    } finally {
        if (yield cancelled()) {
            controller.abort();
        }
    }
}

function* callExportJaggedAnalysisResultToMarkManager() {
    const controller = new AbortController();
    const contextState: ContextState = yield select(getContext);
    try {
        const response = yield call(apiService.submitTask, contextState.assessmentRunId, ExportFormat.JAGGED_ANALYSIS,
            'assessment', '', controller.signal);
        if (response.jobId) {
            yield put(apiCallIsFinished());
        }
    } catch (e) {
        console.error(e.message);
        yield cancel();
    } finally {
        if (yield cancelled()) {
            controller.abort();
        }
    }
}

function* callDownloadGradesAsExcel() {
    const controller = new AbortController();
    const contextState: ContextState = yield select(getContext);
    try {
        const res = yield call(apiService.exportGrades, contextState.assessmentRunId, controller.signal);

        if (res.success && res.s3url.indexOf('https') !== -1) {
            yield put(fileIsReadyForDownload());
            window.open(res.s3url, '_self');
        }
    } catch (e) {
        console.error(e.message);
        yield cancel();
    } finally {
        if (yield cancelled()) {
            controller.abort();
        }
    }
}

function* callDownloadCandidateReportAsCsv(actionName: OptionsMenuActionsName) {
    const controller = new AbortController();
    const contextState: ContextState = yield select(getContext);
    const exportStudentResults = !(actionName === OptionsMenuActionsName.DOWNLOAD_ANALYSIS_AS_CSV);
    const { assessmentRunId } = contextState;

    try {
        const res = yield call(apiService.downloadCandidateReport,
            assessmentRunId,
            exportStudentResults,
            `${assessmentRunId}_${actionName}.csv`,
            controller.signal);
        if (res && res.fileDownloaded) {
            yield put(fileIsReadyForDownload());
        }
    } catch (e) {
        console.error(e);
        yield cancel();
    } finally {
        if (yield cancelled()) {
            controller.abort();
        }
    }
}

function* handleUploadStatusAlerts(
    numberOfCandidatesWithExplanationsImported: number,
    numberOfCandidatesWithExplanationsSent: number,
    numberOfCandidatesWithExplanationsBlank: number,
    type: string,
) {
    const alertConfigurations = notificationService.predefinedAlertNotifications;
    const hasExplanations = numberOfCandidatesWithExplanationsImported > 0;
    const hasSentAndBlank = numberOfCandidatesWithExplanationsSent > 0 && numberOfCandidatesWithExplanationsBlank > 0;

    if (!hasExplanations) {
        if (hasSentAndBlank) {
            yield put(pushAlert(alertConfigurations.uploadFailed(), true));
        } else if (numberOfCandidatesWithExplanationsSent > 0) {
            yield put(pushAlert(alertConfigurations.uploadSuccessWithNote(FileUploadInfo.SENT), true));
        } else if (numberOfCandidatesWithExplanationsBlank > 0) {
            yield put(pushAlert(alertConfigurations.uploadSuccessWithNote(FileUploadInfo.SET), true));
        }
    } else if (hasExplanations) {
        if (hasSentAndBlank) {
            yield put(pushAlert(alertConfigurations.uploadSuccessWithNoteFailed(), true));
        } else if (numberOfCandidatesWithExplanationsSent > 0) {
            yield put(pushAlert(alertConfigurations.uploadSuccessWithNote(FileUploadInfo.SENT), true));
        } else if (numberOfCandidatesWithExplanationsBlank > 0) {
            yield put(pushAlert(alertConfigurations.uploadSuccessWithNote(FileUploadInfo.SET), true));
        } else {
            yield put(pushAlert(alertConfigurations.uploadSuccess(type === FileType.MARKS ? FileUploadInfo.MARKS : FileUploadInfo.GRADE)));
        }
    }
}

function* callUploadFile(action: any, type: string) {
    let ticket: TicketData;
    const contextState: ContextState = yield select(getContext);
    const file = action.payload.file;
    const controller = new AbortController();
    let apiServiceName;
    const alertProps = {
        key: tl('grading.uploadGrade.uploading'),
        info: tl('grading.uploadGrade.uploadingGrades'),
        level: AlertLevel.GREY,
        icon: 'circle notched' as SemanticICONS,
        loading: true,
        payload: {},
    };
    if (type === FileType.MARKS) {
        alertProps.key = tl('grading.uploadMark.uploading');
        alertProps.info = tl('grading.uploadMark.uploadingMarks');
        apiServiceName = apiService.getMarkImportUploadTicket;
    } else if (type === FileType.GRADE) {
        apiServiceName = apiService.getGradeImportExportUploadTicket;
    }
    const sessionState: SessionState = yield select(getSessionState);
    const alertConfigurations = notificationService.predefinedAlertNotifications;
    try {
        ticket = yield call(
            apiServiceName,
            file.name,
            file.size,
            contextState.assessmentRunId,
            controller.signal,
        );
        if (!ticket.alert) {
            yield call(fileUploadService.uploadFile, file, ticket, sessionState.sessionInfo.csrfToken);
            yield put(pushAlert(alertProps, true));
            const task = yield call(apiService.submitTask, contextState.assessmentRunId, action.payload.actionName, 'export', ticket.ticket);
            while (true) {
                const status = yield call(apiService.checkStatus, task.jobId, controller.signal);
                yield delay(2000);
                if (status && status.progress === 100) {
                    yield put(removeAlert(alertProps as Alert));
                    const statusInfo = JSON.parse(status.status);
                    const uploadStatus = statusInfo.status;
                    if (uploadStatus === 'success') {
                        const {
                            numberOfCandidatesWithScoresImported, numberOfCandidatesWithScoresNotImported,
                            numberOfCandidatesWithExplanationsSent, numberOfCandidatesWithExplanationsBlank,
                            numberOfCandidatesWithExplanationsImported, importedGrades, fileContentNotSupportedError,
                        } = statusInfo;
                        const hasExplanations = numberOfCandidatesWithExplanationsImported > 0;

                        if (fileContentNotSupportedError) {
                            yield put(pushAlert(alertConfigurations.fileError(FileErrorTypes.FILE_CONTENT_NOT_SUPPORTED_ERROR), true));
                        }
                        if (type === FileType.MARKS) {
                            yield put(onMarkIsUpdated());
                            if (numberOfCandidatesWithScoresImported > 0 && !hasExplanations) {
                                yield put(pushAlert(alertConfigurations.markUploadSuccess(
                                    numberOfCandidatesWithScoresImported,
                                    numberOfCandidatesWithScoresNotImported
                                )));
                            } else {
                                yield call(handleUploadStatusAlerts,
                                    numberOfCandidatesWithExplanationsImported,
                                    numberOfCandidatesWithExplanationsSent,
                                    numberOfCandidatesWithExplanationsBlank,
                                    type);
                            }
                        } else if (type === FileType.GRADE) {
                            yield put(onGradeIsUpdated());
                            if (importedGrades > 0 && !hasExplanations) {
                                yield put(pushAlert(alertConfigurations.gradeUploadSuccess()));
                            } else {
                                yield call(handleUploadStatusAlerts,
                                    numberOfCandidatesWithExplanationsImported,
                                    numberOfCandidatesWithExplanationsSent,
                                    numberOfCandidatesWithExplanationsBlank,
                                    type);
                            }
                        }
                    } else if (uploadStatus === 'failed') {
                        const fileErrorTypes = Object.values(FileErrorTypes);
                        for (const key of fileErrorTypes) {
                            if (statusInfo[key]) {
                                yield put(pushAlert(alertConfigurations.fileError(key), true));
                                break;
                            }
                        }
                        yield put({ type: FileUploadTypeKeys.HANDLE_CANCELLED });
                    }
                    break;
                }
            }
        } else {
            yield put({ type: FileUploadTypeKeys.HANDLE_CANCELLED });
        }
    } catch (error) {
        let failureMessage;
        if (type === FileType.MARKS) {
            failureMessage = tl('grading.uploadMark.failed');
        } else if (type === FileType.GRADE) {
            failureMessage = tl('grading.uploadGrade.failed');
        }
        const alertProps = {
            key: failureMessage,
            info: '',
            level: AlertLevel.ERROR,
            payload: {},
        };
        yield put(pushAlert(alertProps));
        console.error(error.message);
        yield cancel();
    } finally {
        if (yield cancelled()) {
            yield call(fileUploadService.cancelUpload, ticket, sessionState.sessionInfo.csrfToken);
            controller.abort();
        }
    }
}

function* callUploadMarkSchemeFile(action: any) {
    const contextState: ContextState = yield select(getContext);
    const file = action.payload.file;

    try {
        const response: any = yield call(
            apiService.getMarkSchemeUploadTicket,
            contextState.assessmentRunId,
            true,
            1,
        );
        const ticket = response.tickets[0];
        yield call(apiService.uploadMarkSchemeFile, contextState.assessmentRunId, ticket, file);
        const res = yield call(apiService.storeMarkSchemeFile, contextState.assessmentRunId, ticket, file.name.split('.')[0]);
        if (res && res.StoreUploadedFile && res.StoreUploadedFile.contentRevision) {
            const { title, contentItemId, extension } = res.StoreUploadedFile.contentRevision;
            const alertProps = {
                key: tl('grading.alerts.MARK_SCHEME_UPLOAD_COMPLETE_TITLE'),
                info: '',
                level: AlertLevel.SUCCESS,
                payload: {},
            };
            yield put(onMarkSchemeFileUploaded({ title, contentItemId, extension }));
            yield put(pushAlert(alertProps));
        }
    } catch (error) {
        console.error(error.message);
    }
}

function* removeMarkSchemeFile() {
    const contextState: ContextState = yield select(getContext);

    try {
        const res = yield call(apiService.removeMarkSchemeFile, contextState.assessmentRunId);
        if (res && res.success) {
            yield put(resetMarkSchemeFileUpload());
        }
    } catch (error) {
        console.error(error.message);
        yield cancel();
    }
}

function* callDownloadMarkSchemeFile() {
    const optionsMenuState = yield select((state: RootState) => state.optionsMenu);
    const { contentItemId } = optionsMenuState.markSchemeFile;
    window.open(`/file/ci/${contentItemId}/?download=true`, '_self');
}

function* replaceMarkSchemeFile(action: any) {
    yield call(removeMarkSchemeFile);
    yield call(callUploadMarkSchemeFile, action);
}

function* callSimplePostRequestWithAssessmentId(actionName: OptionsMenuActionsName) {
    function* simplePostRequestWithAssessmentId(apiName: any) {
        const controller = new AbortController();
        const contextState: ContextState = yield select(getContext);
        try {
            yield call(apiName, contextState.assessmentRunId, controller.signal);
            yield put(apiCallIsFinished());
        } catch (e) {
            console.error(e.message);
        } finally {
            if (yield cancelled()) {
                controller.abort();
            }
        }
    }
    switch (actionName) {
    case OptionsMenuActionsName.CLOSE_TEST_TO_START_GRADING:
        yield call(simplePostRequestWithAssessmentId, apiService.closeTest);
        break;
    case OptionsMenuActionsName.SHOW_CANDIDATES_REAL_NAMES:
        yield call(simplePostRequestWithAssessmentId, apiService.deAnonymizeCandidates);
        break;
    }
}
function* callConfirmGrading(actionName: OptionsMenuActionsName,) {
    function* confirmOwnGrading(confirmFinalGrading: boolean, approve: boolean, approveOnBehalf: boolean) {
        const controller = new AbortController();
        const contextState: ContextState = yield select(getContext);
        const { assessmentRunId, selectedCommitteeId } = contextState;

        try {
            yield call(apiService.confirmOwnGrading,
                assessmentRunId,
                approve,
                selectedCommitteeId,
                approveOnBehalf, // only planner action
                confirmFinalGrading,
                controller.signal);
            yield put(apiCallIsFinished());
        } catch (e) {
            console.error(e.message);
        } finally {
            if (yield cancelled()) {
                controller.abort();
            }
        }
    }

    switch (actionName) {
    case OptionsMenuActionsName.BYPASS_PRIVATE_GRADING:
        yield call(confirmOwnGrading, false, true, true);
        break;
    case OptionsMenuActionsName.UNAPPROVE_OWN_GRADES:
        yield call(confirmOwnGrading, false, false, false);
        break;
    case OptionsMenuActionsName.CONFIRM_GRADES_ON_BEHALF_OF_GRADERS:
        yield call(confirmOwnGrading, true, true, true);
        break;
    case OptionsMenuActionsName.UNCONFIRM_FINAL_GRADES:
        yield call(confirmOwnGrading, true, false, false);
        break;
    }
}
function* callReopenGrading() {
    const controller = new AbortController();
    const contextState: ContextState = yield select(getContext);
    const { assessmentRunId, selectedCommitteeId } = contextState;

    try {
        yield call(apiService.reopenGrading,
            assessmentRunId,
            selectedCommitteeId,
            controller.signal);
        yield put(apiCallIsFinished());
    } catch (e) {
        console.error(e.message);
    } finally {
        if (yield cancelled()) {
            controller.abort();
        }
    }
}

function* callFileUploadCompleted() {
    yield put(onFileIsUploaded());
}

export function* callOnMenuItemClick(action: any) {
    const actionName: string = action.payload;

    switch (actionName) {
    case OptionsMenuActionsName.PRINT_AND_DOWNLOAD_QUESTION_SET:
        yield call(downloadAndPrintQuestionSet, actionName);
        break;
    case OptionsMenuActionsName.DOWNLOAD_AND_PRINT_ALL_SUBMISSIONS_PDF:
    case OptionsMenuActionsName.DOWNLOAD_AND_PRINT_COMMITTEE_SUBMISSIONS_PDF:
        yield call(callOpenDownloadAndPrintOverlay, actionName);
        break;
    case OptionsMenuActionsName.OPEN_WORKFLOW_IN_SHARE:
    case OptionsMenuActionsName.OPEN_WORKFLOW_IN_ALIGN:
    case OptionsMenuActionsName.OPEN_WORKFLOW_IN_CONFIRM:
    case OptionsMenuActionsName.OPEN_WORKFLOW_SETTINGS:
    case OptionsMenuActionsName.MANAGE_COMMITTEE_MEMBERS:
        yield call(callOpenWorkflowOverlay, actionName);
        break;
    case OptionsMenuActionsName.OPEN_GRADES_THRESHOLD_EDITOR:
    case OptionsMenuActionsName.OPEN_GRADES_THRESHOLD_EDITOR_UPDATE:
        yield call(callOpenThresholdOverlay, actionName);
        break;
    case OptionsMenuActionsName.OPEN_QUESTION_WEIGHTS_EDITOR:
        yield call(callOpenQuestionWeightsOverlay);
        break;
    case OptionsMenuActionsName.OPEN_QUESTION_ASSIGNMENTS_EDITOR:
        yield call(callOpenQuestionAssignmentsOverlay);
        break;
    case OptionsMenuActionsName.OPEN_COMMITTEE_SELECTOR:
        yield call(callOpenCommitteeSelectOverlay);
        break;
    case OptionsMenuActionsName.EXPORT_JAGGED_ANALYSIS_RESULT_TO_MARK_MANAGER:
        yield call(callExportJaggedAnalysisResultToMarkManager);
        break;
    case OptionsMenuActionsName.DOWNLOAD_EXCEL_MARKING_SHEET:
        yield call(callDownloadExcelMarkingSheet);
        break;
    case OptionsMenuActionsName.DOWNLOAD_MARKS_AS_EXCEL:
    case OptionsMenuActionsName.DOWNLOAD_MARKS_AND_GRADES_AS_CSV:
    case OptionsMenuActionsName.DOWNLOAD_TOTAL_MARKS_AND_GRADES_AS_CSV:
    case OptionsMenuActionsName.DOWNLOAD_MARKS_AND_CRITERIA:
    case OptionsMenuActionsName.DOWNLOAD_MULTIPLE_ATTEMPTS_ALL_DATA_AS_CSV:
    case OptionsMenuActionsName.DOWNLOAD_MULTIPLE_ATTEMPTS_FINAL_DATA_AS_CSV:
        yield call(callGetAssessmentRunExports, actionName);
        break;
    case OptionsMenuActionsName.DOWNLOAD_GRADES_AS_EXCEL:
        yield call(callDownloadGradesAsExcel);
        break;
    case OptionsMenuActionsName.DOWNLOAD_ANALYSIS_AS_CSV:
    case OptionsMenuActionsName.DOWNLOAD_CANDIDATE_REPORT_AS_CSV:
        yield call(callDownloadCandidateReportAsCsv, action.payload);
        break;
    case OptionsMenuActionsName.CLOSE_TEST_TO_START_GRADING:
    case OptionsMenuActionsName.SHOW_CANDIDATES_REAL_NAMES:
        yield call(callSimplePostRequestWithAssessmentId, actionName);
        break;
    case OptionsMenuActionsName.BYPASS_PRIVATE_GRADING:
    case OptionsMenuActionsName.CONFIRM_GRADES_ON_BEHALF_OF_GRADERS:
    case OptionsMenuActionsName.UNAPPROVE_OWN_GRADES:
    case OptionsMenuActionsName.UNCONFIRM_FINAL_GRADES:

        yield call(callConfirmGrading, actionName);
        break;
    case OptionsMenuActionsName.REOPEN_GRADING:
        yield call(callReopenGrading);
        break;
    case OptionsMenuActionsName.REMOVE_MARK_SCHEME:
        yield call(removeMarkSchemeFile);
        break;
    case OptionsMenuActionsName.DOWNLOAD_MARK_SCHEME:
        yield call(callDownloadMarkSchemeFile);
        break;
    case OptionsMenuActionsName.OPEN_ANSWER_KEY_CORRECTION_EDITOR:
        yield call(callOpenQuestionAnswerKeyCorrectionOverlay);
        break;
    }
}

function* callOncancelAction() {
    if (fileUploadTask) {
        yield cancel(fileUploadTask);
    }
}

function* callUpdateCommitteeModeration(action: any) {
    try {
        const contextState: ContextState = yield select(getContext);
        const { partyState } = contextState;
        const committeeId = getPartyCommitteeIdForContext(partyState.gradingPartyDocument);
        if (!committeeId || committeeId <= 0) {
            console.error('Skipping committee moderation update due to invalid committeeId:', committeeId);
            return;
        }
        const { moderationStatus, comment } = action.payload;
        const response = yield call(apiService.updateCommitteeModeration, moderationStatus, committeeId, comment);
        if (response?.success) {
            yield put(updateWorkflowInfo({ moderationStatus }));
        }
    } catch (e) {
        console.error('Failed to update committee moderation', e);
    } finally {
        yield put(apiCallIsFinished());
    }
}

/* WATCHERS */
function* watchOnMenuItemClick() {
    const menuItemClickActionChanel = yield actionChannel(OptionMenuActions.ON_MENU_ITEM_CLICK);

    while (true) {
        const menuItemClickAction = yield take(menuItemClickActionChanel);
        yield fork(callOnMenuItemClick, menuItemClickAction);
    }
}

function* watchOnFileReadyForUpload() {
    while (true) {
        const fileUploadAction = yield take(OptionMenuActions.ON_FILE_IS_READY_FOR_UPLOAD);

        if (fileUploadAction.payload.actionName === OptionsMenuActionsName.UPLOAD_GRADES_FROM_EXCEL) {
            fileUploadTask = yield fork(callUploadFile, fileUploadAction, FileType.GRADE);
        }
        if (fileUploadAction.payload.actionName === OptionsMenuActionsName.MARKS_IMPORT_AS_EXCEL) {
            fileUploadTask = yield fork(callUploadFile, fileUploadAction, FileType.MARKS);
        }

        if (fileUploadAction.payload.actionName === OptionsMenuActionsName.UPLOAD_MARK_SCHEME) {
            fileUploadTask = yield fork(callUploadMarkSchemeFile, fileUploadAction);
        }

        if (fileUploadAction.payload.actionName === OptionsMenuActionsName.REPLACE_MARK_SCHEME) {
            fileUploadTask = yield fork(replaceMarkSchemeFile, fileUploadAction);
        }
    }
}
function* watchOnFileUploadCompleted() {
    yield takeLatest([FileUploadTypeKeys.HANDLE_FILE_UPLOAD_COMPLETED, FileUploadTypeKeys.HANDLE_CANCELLED], callFileUploadCompleted);
}
function* watchOnCancelAction() {
    yield takeLatest([OptionMenuActions.ON_CANCEL_ACTION, OptionMenuActions.CLOSE_OPTIONS_MENU], callOncancelAction);
}

function* watchOnModerationActionClick() {
    yield takeLatest(ContextTypeKeys.ON_MODERATION_ACTION_CLICK, callUpdateCommitteeModeration);
}

export function* rootOptionsMenuSaga() {
    yield all([
        watchOnMenuItemClick(),
        watchOnFileReadyForUpload(),
        watchOnFileUploadCompleted(),
        watchOnCancelAction(),
        watchOnModerationActionClick(),
    ]);
}
